import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Public API calls
export const getRegions = async () => {
  const response = await axios.get(`${API}/regions`);
  return response.data.regions;
};

export const getCategories = async () => {
  const response = await axios.get(`${API}/categories`);
  return response.data.categories;
};

export const getTradies = async (region, category) => {
  const params = new URLSearchParams();
  if (region) params.append('region', region);
  if (category) params.append('category', category);
  
  const response = await axios.get(`${API}/tradies?${params.toString()}`);
  return response.data;
};

export const getTradie = async (id) => {
  const response = await axios.get(`${API}/tradies/${id}`);
  return response.data;
};

export const registerTradie = async (data) => {
  const response = await axios.post(`${API}/tradies/register`, data);
  return response.data;
};

export const uploadLogo = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await axios.post(`${API}/upload-logo`, form);
  // Return relative path; consumers prepend BACKEND_URL for display
  return {
    logo_url: response.data.logo_url, // e.g. /api/logos/...
    storage_path: response.data.storage_path
  };
};

// Resolve a stored logo URL (relative) to an absolute URL for <img src>
export const resolveLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (/^https?:\/\//i.test(logoUrl)) return logoUrl;
  return `${BACKEND_URL}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
};

export const submitEnquiry = async (data) => {
  const response = await axios.post(`${API}/enquiries`, data);
  return response.data;
};

// Payment API calls
export const createCheckoutSession = async (tradieId) => {
  const originUrl = window.location.origin;
  const response = await axios.post(`${API}/payments/create-checkout`, {
    tradie_id: tradieId,
    origin_url: originUrl
  });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await axios.get(`${API}/payments/status/${sessionId}`);
  return response.data;
};

// Admin API calls
export const adminLogin = async (email, password) => {
  const response = await axios.post(`${API}/admin/login`, { email, password });
  return response.data;
};

export const adminGetTradies = async (token, status) => {
  const params = status ? `?status=${status}` : '';
  const response = await axios.get(`${API}/admin/tradies${params}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminApproveTradie = async (token, tradieId) => {
  const response = await axios.put(`${API}/admin/tradies/${tradieId}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminRejectTradie = async (token, tradieId, reason = null) => {
  const response = await axios.put(`${API}/admin/tradies/${tradieId}/reject`, { reason }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminDeleteTradie = async (token, tradieId) => {
  const response = await axios.delete(`${API}/admin/tradies/${tradieId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminGetEnquiries = async (token, status) => {
  const params = status ? `?status=${status}` : '';
  const response = await axios.get(`${API}/admin/enquiries${params}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminMarkEnquiryRead = async (token, enquiryId) => {
  const response = await axios.put(`${API}/admin/enquiries/${enquiryId}/mark-read`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminGetStats = async (token) => {
  const response = await axios.get(`${API}/admin/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminGetExpiredCount = async (token) => {
  const response = await axios.get(`${API}/admin/tradies/expired-count`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const adminExtendTrials = async (token, days = 90, onlyExpired = true) => {
  const response = await axios.post(`${API}/admin/tradies/extend-trials`,
    { days, only_expired: onlyExpired },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const adminGetAnalytics = async (token, days = 30) => {
  const response = await axios.get(`${API}/admin/analytics`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { days }
  });
  return response.data;
};

export const adminGetTradieReport = async (token, tradieId, year, month) => {
  const response = await axios.get(`${API}/admin/tradie-report/${tradieId}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { year, month }
  });
  return response.data;
};

export const adminSendTradieReport = async (token, tradieId, year, month) => {
  const response = await axios.post(`${API}/admin/tradie-report/${tradieId}/send`, null, {
    headers: { Authorization: `Bearer ${token}` },
    params: { year, month }
  });
  return response.data;
};

export const adminSendAllTradieReports = async (token, year, month, onlyWithActivity = false) => {
  const response = await axios.post(`${API}/admin/tradie-reports/send-all`, null, {
    headers: { Authorization: `Bearer ${token}` },
    params: { year, month, only_with_activity: onlyWithActivity }
  });
  return response.data;
};

// Public tracking: fire-and-forget tradie interaction logging
export const trackTradieEvent = (tradieId, eventType) => {
  axios.post(`${API}/track/tradie-event`, {
    tradie_id: tradieId,
    event_type: eventType,
  }).catch(() => { /* analytics best-effort */ });
};

export const adminSetFreeSubscription = async (token, tradieId, isFree) => {
  const response = await axios.put(`${API}/admin/tradies/${tradieId}/set-free`, 
    { is_free: isFree },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const adminSendTrialWarnings = async (token) => {
  const response = await axios.post(`${API}/admin/send-trial-warnings`, {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const adminGetExpiringTrials = async (token) => {
  const response = await axios.get(`${API}/admin/trial-expiring`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

// Tradie Portal API calls
export const tradieLogin = async (email, password) => {
  const response = await axios.post(`${API}/tradie/login`, { email, password });
  return response.data;
};

export const tradieSetPassword = async (email, password) => {
  const response = await axios.post(`${API}/tradie/set-password?email=${encodeURIComponent(email)}`, { password });
  return response.data;
};

export const tradieGetProfile = async (token) => {
  const response = await axios.get(`${API}/tradie/profile`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const tradieUpdateProfile = async (token, data) => {
  const response = await axios.put(`${API}/tradie/profile`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const tradieGetEnquiries = async (token) => {
  const response = await axios.get(`${API}/tradie/enquiries`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const tradieMarkEnquiryRead = async (token, enquiryId) => {
  const response = await axios.put(`${API}/tradie/enquiries/${enquiryId}/mark-read`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const tradieGetSubscription = async (token) => {
  const response = await axios.get(`${API}/tradie/subscription`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const tradieCancelSubscription = async (token) => {
  const response = await axios.post(`${API}/tradie/subscription/cancel`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const tradieReactivateSubscription = async (token) => {
  const response = await axios.post(`${API}/tradie/subscription/reactivate`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};
